import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BottomNavigation, BottomNavigationAction, Badge } from '@mui/material';
import { Newspaper, Search, LocationOn, Person, Bolt } from '@mui/icons-material';
import { getLocationOn, getUnreadMessagesCount, Colors } from '../../helpers/navigationHelpers';
import styles from './CoreTabNavigator.module.css';

const CoreTabNavigator = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(0);
  const [locationOn, setLocationOn] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bottomInset, setBottomInset] = useState(0);

  // Animation refs for AI tab
  const breatheAnim = useRef(null);
  const shimmerAnim = useRef(null);

  useEffect(() => {
    // Update locationOn state
    setLocationOn(getLocationOn());
    // Update unread messages count
    setUnreadCount(getUnreadMessagesCount());

    // Detect bottom inset (safe area)
    const detectBottomInset = () => {
      // Create a test element to measure safe area inset
      const testEl = document.createElement('div');
      testEl.style.position = 'fixed';
      testEl.style.bottom = '0';
      testEl.style.left = '0';
      testEl.style.right = '0';
      testEl.style.paddingBottom = 'env(safe-area-inset-bottom, 0px)';
      testEl.style.visibility = 'hidden';
      testEl.style.pointerEvents = 'none';
      document.body.appendChild(testEl);
      
      const computedStyle = window.getComputedStyle(testEl);
      const paddingBottom = computedStyle.paddingBottom;
      const insetValue = parseFloat(paddingBottom) || 0;
      
      document.body.removeChild(testEl);
      
      // Fallback: detect using visual viewport API if available and CSS env didn't work
      if (insetValue === 0 && window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const calculatedInset = Math.max(0, windowHeight - viewportHeight);
        setBottomInset(calculatedInset);
      } else {
        setBottomInset(insetValue);
      }
    };

    // Initial detection
    detectBottomInset();

    // Listen for storage changes
    const handleStorageChange = () => {
      setLocationOn(getLocationOn());
      setUnreadCount(getUnreadMessagesCount());
    };

    // Listen for viewport changes (orientation, keyboard, etc.)
    const handleViewportChange = () => {
      detectBottomInset();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('orientationchange', handleViewportChange);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('orientationchange', handleViewportChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
    };
  }, []);

  // Update selected tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/feed') || path.includes('/chats')) {
      setValue(0);
    } else if (path.includes('/search')) {
      setValue(1);
    } else if (path.includes('/create-reel')) {
      setValue(2);
    } else if (path.includes('/location')) {
      setValue(3);
    } else if (path.includes('/profile')) {
      setValue(4);
    }
  }, [location.pathname]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    
    switch (newValue) {
      case 0:
        navigate('/app/feed');
        break;
      case 1:
        navigate('/app/search');
        break;
      case 2:
        navigate('/app/create-reel');
        break;
      case 3:
        navigate('/app/location');
        break;
      case 4:
        navigate('/app/profile');
        break;
      default:
        break;
    }
  };

  const primaryColor = locationOn ? Colors.CUSTOM_YELLOW : Colors.PRIMARY;
  const activeBackgroundColor = locationOn ? 'white' : 'white';
  const inactiveBackgroundColor = primaryColor;

  const bottomNavHeight = 70;

  return (
    <div className={styles.container} style={{ paddingBottom: 70 }}>
      <div 
        className={styles.content}
        style={{
          paddingBottom: `${bottomNavHeight + bottomInset}px`,
        }}
      >
        {children}
      </div>
      <BottomNavigation
        value={value}
        onChange={handleChange}
        showLabels={false}
        className={styles.bottomNavigation}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: primaryColor,
          height: `${bottomNavHeight + bottomInset}px`,
          minHeight: `${bottomNavHeight}px`,
          borderTop: 'none',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          paddingBottom: `${bottomInset}px`,
          zIndex: 1000,
          boxSizing: 'border-box',
        }}
      >
        {/* Feed/Chats Tab */}
        <BottomNavigationAction
          icon={
            <Badge badgeContent={unreadCount} color="error">
              <Newspaper />
            </Badge>
          }
          sx={{
            backgroundColor: value === 0 ? activeBackgroundColor : inactiveBackgroundColor,
            color: value === 0 ? primaryColor : 'white',
            minWidth: 'auto',
            '& .MuiSvgIcon-root': {
              fontSize: 27,
            },
            '&.Mui-selected': {
              color: primaryColor,
            },
          }}
        />

        {/* Search Tab */}
        <BottomNavigationAction
          icon={<Search />}
          sx={{
            backgroundColor: value === 1 ? activeBackgroundColor : inactiveBackgroundColor,
            color: value === 1 ? primaryColor : 'white',
            minWidth: 'auto',
            '& .MuiSvgIcon-root': {
              fontSize: 27,
            },
            '&.Mui-selected': {
              color: primaryColor,
            },
          }}
        />

        {/* Create Reel Tab - Special Lightning Bolt */}
        <BottomNavigationAction
          icon={
            <div className={`${styles.aiTabContainer} ${value === 2 ? styles.aiTabActive : ''}`}>
              <div 
                className={styles.aiTabGradient}
                style={{
                  background: value === 2 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)'
                    : locationOn 
                      ? `linear-gradient(135deg, ${Colors.CUSTOM_YELLOW} 0%, #FFD700 50%, ${Colors.CUSTOM_YELLOW} 100%)`
                      : `linear-gradient(135deg, ${Colors.PRIMARY} 0%, #0066CC 50%, ${Colors.PRIMARY} 100%)`
                }}
              >
                <Bolt sx={{ fontSize: 28, color: 'white' }} />
              </div>
            </div>
          }
          sx={{
            backgroundColor: primaryColor,
            minWidth: 'auto',
          }}
        />

        {/* Location Tab */}
        <BottomNavigationAction
          icon={<LocationOn />}
          sx={{
            backgroundColor: value === 3 ? activeBackgroundColor : inactiveBackgroundColor,
            color: value === 3 ? primaryColor : 'white',
            minWidth: 'auto',
            '& .MuiSvgIcon-root': {
              fontSize: 24,
            },
            '&.Mui-selected': {
              color: primaryColor,
            },
          }}
        />

        {/* Profile Tab */}
        <BottomNavigationAction
          icon={<Person />}
          sx={{
            backgroundColor: value === 4 ? activeBackgroundColor : inactiveBackgroundColor,
            color: value === 4 ? primaryColor : 'white',
            minWidth: 'auto',
            '& .MuiSvgIcon-root': {
              fontSize: 27,
            },
            '&.Mui-selected': {
              color: primaryColor,
            },
          }}
        />
      </BottomNavigation>
    </div>
  );
};

export default CoreTabNavigator;

